import React from 'react';
import { SchematicShell, Line } from './SchematicPrimitives';

/**
 * Elevation schematic for `dorm-dresser`.
 *
 * Front: top band, base band, 3 equal drawer faces separated by thin
 * gap lines, each with a curved arc pull centered horizontally.
 * Side: top band + base band with the base recessed from the front edge.
 * Right mirrors left.
 *
 * Constants match dormDresserRenderer.js.
 */

const IN = 0.0254;
const TOP_THICK = 1 * IN;
const BASE_H = 2.5 * IN;
const BASE_RECESS = 0.75 * IN;
const DRAWER_GAP = 0.4 * IN;
const NUM_DRAWERS = 3;
const PULL_W = 8 * IN;
const ARC_RISE = 1 * IN;

export function DormDresserSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      >
        <g
          transform={
            view === 'right'
              ? `scale(-1, 1) translate(${-depth}, 0)`
              : undefined
          }
        >
          <DresserSide
            depth={depth}
            height={height}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        </g>
      </SchematicShell>
    );
  }

  // FRONT
  const bodyTop = TOP_THICK;
  const bodyBottom = height - BASE_H;
  const bodyH = bodyBottom - bodyTop;
  const drawerH = (bodyH - (NUM_DRAWERS - 1) * DRAWER_GAP) / NUM_DRAWERS;

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {/* Top band */}
      <Line
        x1={0}
        y1={bodyTop}
        x2={width}
        y2={bodyTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Base band */}
      <Line
        x1={0}
        y1={bodyBottom}
        x2={width}
        y2={bodyBottom}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Drawer gap lines */}
      {Array.from({ length: NUM_DRAWERS - 1 }, (_, g) => {
        const y = bodyTop + (g + 1) * drawerH + g * DRAWER_GAP + DRAWER_GAP / 2;
        return (
          <Line
            key={`gap-${g}`}
            x1={0}
            y1={y}
            x2={width}
            y2={y}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
      })}
      {/* Arc pulls on each drawer */}
      {Array.from({ length: NUM_DRAWERS }, (_, d) => {
        const cy = bodyTop + d * (drawerH + DRAWER_GAP) + drawerH / 2;
        const x1 = (width - PULL_W) / 2;
        const x2 = (width + PULL_W) / 2;
        const cx = width / 2;
        return (
          <path
            key={`pull-${d}`}
            d={`M ${x1} ${cy} Q ${cx} ${cy - ARC_RISE} ${x2} ${cy}`}
            fill="none"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        );
      })}
    </SchematicShell>
  );
}

function DresserSide({ depth, height, strokeColor, strokeWidth }) {
  const bodyTop = TOP_THICK;
  const bodyBottom = height - BASE_H;

  return (
    <g>
      {/* Top band */}
      <Line
        x1={0}
        y1={bodyTop}
        x2={depth}
        y2={bodyTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Base band — recessed from the front edge */}
      <Line
        x1={0}
        y1={bodyBottom}
        x2={depth - BASE_RECESS}
        y2={bodyBottom}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Vertical edge of base recess */}
      <Line
        x1={depth - BASE_RECESS}
        y1={bodyBottom}
        x2={depth - BASE_RECESS}
        y2={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

DormDresserSchematic.supportsSideViews = true;
